import React, { useState, useMemo, useEffect, useCallback, useRef } from "react";
import {
  Card,
  Dropdown,
} from "react-bootstrap";
import ConfirmationModal from "@enerex/unified-ui-react/ConfirmationModal";
import UploadFileModal from "../../components/UploadFileModal";
import JsonViewerModal from "../../components/JsonViewerModal";
import { AI_SERVICES_URL, DEV_URL, rget, rdelete, rpost } from "../../utils/requests";
import { trimInvalidUTCDate } from "../../helpers/api/utils";
import getConfig from "../../config";
import DeleteIcon from "@enerex/unified-ui-react/icons/DeleteIcon";
import DocumentsIcon from "@enerex/unified-ui-react/icons/DocumentsIcon";
import DownloadIcon from "@enerex/unified-ui-react/icons/DownloadIcon";
import LoadingIndicator from "@enerex/unified-ui-react/LoadingIndicator";
import ShowIcon from "@enerex/unified-ui-react/icons/ShowIcon";
import SyncIcon from "@enerex/unified-ui-react/icons/SyncIcon";
import UploadIcon from "@enerex/unified-ui-react/icons/UploadIcon";
import TableSearch from 'components/Table/TableSearch';
import LoadingButton from "../../components/LoadingButton";
import Table from "@enerex/unified-ui-react/Table";

const COLUMNS = [
  { key: "dateCreated", label: "Request On" },
  { key: "fileName", label: "File Name" },
  { key: "status", label: "Status" },
  { key: "uploadedBy", label: "Initiated By" },
  { key: "actions", label: "Actions" },
];

const PAGE_SIZES = [10, 25, 50, 100];

const normalizeDataAIRow = (item) => ({
  id: item.id,
  fileName: item.fileName || "",
  status: item.status || "",
  uploadedBy: item.uploadedBy || "",
  dateCreated: item.createdAt || "",
  fileUrl: item.fileUrl || "",
  data: item.data || "",
  summary: item.summary || "",
});

const areRowsEqual = (left, right) =>
  left?.id === right?.id &&
  left?.fileName === right?.fileName &&
  left?.status === right?.status &&
  left?.uploadedBy === right?.uploadedBy &&
  left?.dateCreated === right?.dateCreated &&
  left?.fileUrl === right?.fileUrl &&
  left?.data === right?.data &&
  left?.summary === right?.summary;

const mergeRowsById = (currentRows, nextRows) => {
  const currentById = new Map(currentRows.map((row) => [row.id, row]));

  return nextRows.map((nextRow) => {
    const currentRow = currentById.get(nextRow.id);
    return currentRow && areRowsEqual(currentRow, nextRow)
      ? currentRow
      : nextRow;
  });
};

const ContractAI = () => {
  // Helper function to format date using trimInvalidUTCDate
  const formatDate = (dateString) => {
    if (!dateString) return "";
    try {
      const formattedDate = trimInvalidUTCDate(dateString, true); // true for full year
      if (!formattedDate) return "";

      // if (!isNaN(date.getTime())) {
      //   const timeString = date.toLocaleTimeString([], {
      //     hour: "2-digit",
      //     minute: "2-digit",
      //   });
      //   return `${formattedDate} ${timeString}`;
      // }

      return formattedDate;
    } catch (e) {
      return "";
    }
  };
  const [search, setSearch] = useState({ value: "" });
  const [searchInput, setSearchInput] = useState("");

  const [showUpload, setShowUpload] = useState(false);
  const [jsonModalData, setJsonModalData] = useState(null);

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const fetchInProgressRef = useRef(false);

  const [confirmModal, setConfirmModal] = useState({
    show: false,
    id: null,
  });

  const [summaryModal, setSummaryModal] = useState({
    show: false,
    content: "",
    loading: false,
  });

  const summaryAbortRef = useRef(null);

  const fetchData = useCallback(async ({ showLoading = false } = {}) => {
    if (fetchInProgressRef.current) return;
    fetchInProgressRef.current = true;
    if (showLoading) setLoading(true);
    try {
      const res = await rget(
        `${DEV_URL}/api/DataAI/GetAll?dataType=ContractAnalysis`
      );
      const json = res?.data;

      if (json?.success && Array.isArray(json.data)) {
        const nextRows = json.data.map(normalizeDataAIRow);
        setData((currentRows) => mergeRowsById(currentRows, nextRows));
      }
    } catch (e) {
      console.error("Failed to fetch DataAI list", e);
    } finally {
      fetchInProgressRef.current = false;
      if (showLoading) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData({ showLoading: true });
    const interval = setInterval(fetchData, 5000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const fetchJsonFromBlob = async (blobUrl) => {
    if (!blobUrl) return null;

    setJsonLoading(true);
    try {
      // Use backend as proxy to avoid CORS issues with blob URLs
      const response = await rget(
        `${DEV_URL}/api/DataAI/GetBlobContent?blobUrl=${encodeURIComponent(
          blobUrl
        )}`
      );
      if (response && response.status >= 200 && response.status < 300) {
        return JSON.stringify(response.data);
      } else {
        throw new Error(
          `Failed to fetch blob content: ${response?.status || "unknown"}`
        );
      }
    } catch (e) {
      console.error("Failed to fetch JSON from blob URL", e);
      return `Error loading data: ${e.message}`;
    } finally {
      setJsonLoading(false);
    }
  };

  const handleShowJson = async (blobUrl) => {
    const jsonData = await fetchJsonFromBlob(blobUrl);
    setJsonModalData(jsonData);
  };

  const handleDownloadJson = async (blobUrl, fileName) => {
    const jsonData = await fetchJsonFromBlob(blobUrl);
    if (
      !jsonData ||
      (typeof jsonData === "string" && jsonData.startsWith("Error"))
    ) return;
    const content = typeof jsonData === "string" ? jsonData : JSON.stringify(jsonData, null, 2);
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fileName || "response"}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleSummary = async (blobUrl, rowId, existingSummary) => {
    if (existingSummary) {
      setSummaryModal({ show: true, content: existingSummary, loading: false });
      return;
    }
    if (summaryAbortRef.current) summaryAbortRef.current.abort();
    const abortController = new AbortController();
    summaryAbortRef.current = abortController;
    setSummaryModal({ show: true, content: "", loading: true });
    try {
      const jsonData = await fetchJsonFromBlob(blobUrl);
      if (abortController.signal.aborted) return;
      if (!jsonData || (typeof jsonData === "string" && jsonData.startsWith("Error"))) {
        setSummaryModal({ show: true, content: "Something went wrong. Please try again later. The summary data could not be processed.", loading: false });
        return;
      }
      const payload = typeof jsonData === "string" ? JSON.parse(jsonData) : jsonData;
      const response = await rpost(`${AI_SERVICES_URL}/api/contracts/v1/summary`, payload);
      if (abortController.signal.aborted) return;
      if (response && response.status >= 200 && response.status < 300) {
        const summaryText = response.data?.summary || JSON.stringify(response.data);
        setSummaryModal({ show: true, content: summaryText, loading: false });
        try {
          await rpost(`${DEV_URL}/api/DataAI/SaveSummary`, { Id: rowId, Summary: summaryText });
        } catch (err) {
          console.error("Failed to save summary", err);
        }
      } else {
        setSummaryModal({ show: true, content: "Something went wrong. Please try again later. The summary data could not be processed.", loading: false });
      }
    } catch (e) {
      if (abortController.signal.aborted) return;
      console.error("Summary failed", e);
      setSummaryModal({ show: true, content: "Something went wrong. Please try again later. The summary data could not be processed.", loading: false });
    }
  };

  const openDeleteConfirm = (id) => {
    setConfirmModal({
      show: true,
      id,
    });
  };

  const closeDeleteConfirm = () => {
    setConfirmModal({
      show: false,
      id: null,
    });
  };

  const closeSummaryModal = () => {
    if (summaryAbortRef.current) summaryAbortRef.current.abort();
    setSummaryModal({ show: false, content: "", loading: false });
  };

  const handleDelete = async (id) => {
    if (!id) return;

    try {
      setDeletingId(id);
      await rdelete(`${DEV_URL}/api/DataAI/Delete/${id}`);
      setData((currentRows) => currentRows.filter((row) => row.id !== id));
      await fetchData();
      closeDeleteConfirm();
    } catch (e) {
      console.error("Failed to delete record", e);
      alert("Failed to delete record.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleConfirmDelete = async () => {
    await handleDelete(confirmModal.id);
  };

  const filtered = useMemo(() => {
    // A fresh array also resets Unified UI pagination when resubmitting the same search.
    let rows = [...data];

    if (search.value.trim()) {
      const q = search.value.toLowerCase();

      rows = rows.filter((r) =>
        COLUMNS.filter((c) => c.key !== "actions").some((c) =>
          String(r[c.key] || "")
            .toLowerCase()
            .includes(q)
        )
      );
    }

    return rows;
  }, [data, search]);

  const handleUploadFiles = () => {
    fetchData({ showLoading: true });
  };

  const tableColumns = [
    {
      id: "dateCreated",
      header: "Request On",
      accessorFn: (row) => {
        const date = new Date(row.dateCreated);
        return isNaN(date.getTime()) ? 0 : date.getTime();
      },
      cell: ({ row }) => formatDate(row.original.dateCreated),
    },
    {
      header: "File Name",
      accessorKey: "fileName",
      cell: ({ row }) =>
        row.original.fileUrl ? (
          <a
            href={row.original.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
          >
            {row.original.fileName}
          </a>
        ) : (
          row.original.fileName
        ),
    },
    {
      header: "Status",
      accessorKey: "status",
      cell: ({ row }) =>
        row.original.status?.toLowerCase() === "running" ? (
          <span className="d-inline-flex align-items-center gap-1">
            <LoadingIndicator width={14} height={14} />
            {row.original.status}
          </span>
        ) : row.original.status?.toLowerCase() === "ai_response_received" ? (
          "Success"
        ) : (
          "Failed"
        ),
    },
    {
      header: "Initiated By",
      accessorKey: "uploadedBy",
    },
    {
      id: "actions",
      header: "Actions",
      enableSorting: false,
      enableHiding: false,
      cell: ({ row: tableRow }) => {
        const row = tableRow.original;

        return (
          <Dropdown drop="down" align="end" className="position-static">
            <Dropdown.Toggle
              variant="link"
              className="btn btn-sm btn-link arrow-none p-0 shadow-none text-muted"
              style={{
                fontSize: "1.2rem",
                lineHeight: "1",
                cursor: "pointer",
              }}
            >
              ⋮
            </Dropdown.Toggle>
            <Dropdown.Menu
              renderOnMount
              popperConfig={{ strategy: "fixed" }}
              style={{
                zIndex: 9999,
                minWidth: "140px",
              }}
            >
              {row.data && (
                <Dropdown.Item
                  onClick={() => handleShowJson(row.data)}
                  className="d-flex align-items-center gap-2"
                  style={{ cursor: "pointer" }}
                >
                  <ShowIcon />
                  {jsonLoading ? "Loading..." : "View Response"}
                </Dropdown.Item>
              )}
              {row.data &&
                row.status?.toLowerCase() === "ai_response_received" && (
                  <Dropdown.Item
                    onClick={() => handleDownloadJson(row.data, row.fileName)}
                    className="d-flex align-items-center gap-2"
                    style={{ cursor: "pointer" }}
                  >
                    <DownloadIcon />
                    Download
                  </Dropdown.Item>
                )}
              {row.data &&
                row.status?.toLowerCase() === "ai_response_received" && (
                  <Dropdown.Item
                    onClick={() => handleSummary(row.data, row.id, row.summary)}
                    className="d-flex align-items-center gap-2"
                    style={{ cursor: "pointer" }}
                  >
                    <DocumentsIcon />
                    Summary
                  </Dropdown.Item>
                )}
              <Dropdown.Item
                onClick={() => openDeleteConfirm(row.id)}
                className="d-flex align-items-center gap-2 text-danger"
                disabled={deletingId === row.id}
                style={{
                  cursor: deletingId === row.id ? "not-allowed" : "pointer",
                }}
              >
                <DeleteIcon />
                {deletingId === row.id ? "Deleting..." : "Delete"}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        );
      },
    },
  ];

  return (
    <>
      <UploadFileModal
        show={showUpload}
        onHide={() => setShowUpload(false)}
        onUpload={handleUploadFiles}
        apiUrl={`${AI_SERVICES_URL}/api/contracts/v2/parse`}
        title="Upload Contract Files"
        webhookUrl={`${getConfig().apiUrl}/api/Webhook/contractHook`}
        type="ContractAnalysis"
      />

      <JsonViewerModal
        show={!!jsonModalData}
        onHide={() => setJsonModalData(null)}
        data={jsonModalData || ""}
      />

      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        show={confirmModal.show}
        onHide={closeDeleteConfirm}
        title="Delete Record"
        confirmButtonLabel="Delete"
        confirmButtonVariant="danger"
        isLoading={!!deletingId}
        onConfirm={handleConfirmDelete}
      >
        <p>Are you sure you want to delete this record?</p>
      </ConfirmationModal>

      <ConfirmationModal
        dialogClassName="modal-md"
        centered
        size="xl"
        show={summaryModal.show}
        onHide={closeSummaryModal}
        title="Summary"
        confirmButtonLabel="Close"
        confirmButtonVariant="secondary"
        isLoading={summaryModal.loading}
        onConfirm={closeSummaryModal}
      >
        {summaryModal.loading ? (
          <div className="text-center py-4">
            <LoadingIndicator width={32} height={32} />
            <p className="mt-2">Generating summary...</p>
          </div>
        ) : (
          <p style={{ whiteSpace: "pre-wrap" }}>{summaryModal.content}</p>
        )}
      </ConfirmationModal>

      <div className="d-flex justify-content-between align-items-center mb-3 mt-3">
        <h4
          className="header-title mb-0"
          style={{ color: "#313a46", fontWeight: 600 }}
        >
          Contract Assistant
        </h4>

        <div className="d-flex align-items-center gap-2">
          <LoadingButton
            variant="primary"
            onClick={() => fetchData({ showLoading: true })}
            disabled={loading}
            isLoading={loading}
            iconOrientation="start"
          >
            {!loading && <SyncIcon className="me-1" />}
            Refresh
          </LoadingButton>

          <LoadingButton
            variant="primary"
            isLoading={false}
            className="d-flex align-items-center gap-1"
            onClick={() => setShowUpload(true)}
          >
            <UploadIcon className="me-1" /> Upload Files
          </LoadingButton>
        </div>
      </div>

      <Card>
        <Card.Body style={{ overflow: "visible" }}>
          <Table
            columns={tableColumns}
            data={filtered}
            loading={loading}
            pageSizes={PAGE_SIZES}
            customHeader={
              <div style={{ maxWidth: 300 }}>
                <TableSearch appliedSearch={search.value} value={searchInput} onChange={setSearchInput}
                  onSearch={value => setSearch({ value })} />
              </div>
            }
            enableSearch={false}
            enablePagination
            enableHideColumns
            enableSorting
            enableFilters={false}
            enableSelection={false}
            enablePaginateAll={false}
            enableColumnVisibilityStorage={false}
            enableGlobalFilterStorage={false}
            enableUrlPagination={false}
            noDataMessage="No records found."
          />
        </Card.Body>
      </Card>
    </>
  );
};

export default ContractAI;
